/**
 * Calculates bond order financial details based on price, accrued interest, and face value
 * 
 * @param {Object} data - Object containing order data
 * @param {number} data.price - Bond price
 * @param {number} data.accruedInterest - Accrued interest
 * @param {number} amountPerRung - Amount per rung for the ladder
 * @param {number} faceValue - Face value of the bond (typically 1)
 * @param {string} [logPrefix=''] - Optional prefix for logging
 * @returns {Object} Object containing financial calculations
 */
export const calculateOrderFinancials = (data, amountPerRung, faceValue = 1, logPrefix = '') => {
  const price = parseFloat(data.price) || 0;
  const accruedInterest = parseFloat(data.accruedInterest) || 0;
  const amount = parseFloat(amountPerRung) || 0;
  
  // Formula: ((price + accruedInterest) / 100) * faceValue
  const costPerUnit = ((price + accruedInterest) / 100) * faceValue;
  const rawQty = costPerUnit > 0 ? Math.floor(amount / costPerUnit) : 0;

  // Round to nearest 1,000 with midpoint at 500:
  // 0 - 499 => 0, 500 - 1499 => 1000, 1500 - 2499 => 2000, ...
  const roundToNearestThousand = (n) => {
    if (n < 500) return 0;
    return Math.round(n / 1000) * 1000;
  };

  const qty = roundToNearestThousand(rawQty);
  const totalCost = costPerUnit * qty;
  
  // Debug logging: show raw and rounded qty along with identifying info
  try {
    const debugMsg = `${logPrefix}rawQty=${rawQty}, qty=${qty}, cusip=${data?.cusip ?? 'N/A'}, rungIndex=${data?.rungIndex ?? 'N/A'}`;
    console.log(debugMsg);
  } catch (e) {
    // swallow any unexpected logging errors to avoid interfering with app flow
  }
  
  return { costPerUnit, qty, totalCost, rawQty };
};
